// Décisions sur une variante (§ 9 de la spécification) : retenir (motif si elle n'est pas première), rejeter (motif), reprendre comme
// base de travail (brouillons NON validés : repères de comptage, liaisons, condensats, alimentations, bus, fiches système) et
// validation technique (référent). Chaque bouton n'existe que pour le droit prévu ; un refus prévisible est expliqué avant le clic.
import { useState } from 'react';
import { CheckCircle2, ClipboardCheck, CopyPlus, XCircle } from 'lucide-react';
import { api, messageErreur } from '../../../lib/api.ts';
import { useSession } from '../../../lib/session.tsx';
import { dateHeure } from '../../../lib/format.ts';
import { Alerte, Bouton, ChampTexte, Dialogue, LienBouton, Panneau, useNotifier } from '../../../components/ui.tsx';
import { LIBELLE_STATUT, repriseImpossible, type DetailVue, type LigneVue, type RepriseVue } from './logique.ts';

type Action = 'retenir' | 'rejeter' | 'reprise' | 'validation' | null;

export function Decision({ dossierId, ligne, detail, entreesValidees, rafraichir }: {
  dossierId: string; ligne: LigneVue; detail: DetailVue; entreesValidees: boolean; rafraichir: () => Promise<unknown>;
}) {
  const { peut } = useSession();
  const notifier = useNotifier();
  const [action, setAction] = useState<Action>(null);
  const [motif, setMotif] = useState('');
  const [levees, setLevees] = useState<Record<string, string>>({});
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [reprise, setReprise] = useState<RepriseVue | null>(null);
  const calculer = peut('proposition.calculer');
  const valider = peut('echelle.valider');
  const base = `/api/dossiers/${dossierId}/proposition/${ligne.id}`;
  const refusReprise = repriseImpossible(ligne, entreesValidees && !detail.perimee);
  const motifRequis = (action === 'retenir' || action === 'reprise') && ligne.rang > 1 && ligne.statut === 'calculee';
  const aVerifier = detail.variante.points.filter((p) => p.niveau === 'a_verifier');

  const ouvrir = (a: Action) => { setAction(a); setMotif(''); setErreur(null); setLevees({}); };
  const envoyer = async () => {
    setEnvoi(true); setErreur(null);
    try {
      if (action === 'retenir') { await api.post(`${base}/retenir`, { motif: motif.trim() || null }); notifier('Variante retenue.'); }
      else if (action === 'rejeter') { await api.post(`${base}/rejeter`, { motif: motif.trim() }); notifier('Variante rejetée.'); }
      else if (action === 'validation') {
        await api.post(`${base}/validation`, { levees: Object.fromEntries(Object.entries(levees).filter(([, c]) => c.trim().length >= 3)) });
        notifier('Variante validée.');
      } else if (action === 'reprise') { setReprise(await api.post<RepriseVue>(`${base}/reprise`, { motif: motif.trim() || null })); }
      setAction(null);
      await rafraichir();
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };

  const desactive = (action === 'rejeter' || motifRequis) && motif.trim().length < 3;
  return (
    <Panneau titre="Décision">
      <p className="petit">Statut : <strong>{LIBELLE_STATUT[ligne.statut]}</strong>{ligne.retenueLe ? ` · retenue le ${dateHeure(ligne.retenueLe)}` : ''}{ligne.reprisLe ? ` · reprise le ${dateHeure(ligne.reprisLe)}` : ''}</p>
      {ligne.motifChoix && <p className="tres-petit secondaire">Motif du choix : {ligne.motifChoix}</p>}
      {ligne.motifRejet && <p className="tres-petit secondaire">Motif du rejet : {ligne.motifRejet}</p>}
      {!calculer && !valider && <p className="tres-petit discret" style={{ marginTop: 8 }}>Consultation : retenir, rejeter ou reprendre une variante est réservé au référent technique et au chargé d’affaires.</p>}
      {(calculer || valider) && (
        <div className="groupe-btn" style={{ marginTop: 10 }}>
          {calculer && ['calculee', 'retenue'].includes(ligne.statut) && ligne.statut !== 'retenue' && <Bouton compact icone={<CheckCircle2 aria-hidden />} onClick={() => ouvrir('retenir')}>Retenir</Bouton>}
          {calculer && ligne.origine === 'trame' && ligne.statut !== 'rejetee' && <Bouton compact icone={<CopyPlus aria-hidden />} disabled={!!refusReprise} title={refusReprise ?? undefined}
            onClick={() => ouvrir('reprise')}>{ligne.statut === 'convertie' ? 'Revoir la reprise' : 'Reprendre comme base de travail'}</Bouton>}
          {valider && ligne.statut === 'retenue' && <Bouton compact icone={<ClipboardCheck aria-hidden />} disabled={ligne.criteres.bloquants > 0}
            title={ligne.criteres.bloquants > 0 ? 'Impossible de valider avec un point bloquant ouvert.' : undefined} onClick={() => ouvrir('validation')}>Valider la variante</Bouton>}
          {calculer && !['convertie', 'rejetee'].includes(ligne.statut) && <Bouton compact variante="danger" icone={<XCircle aria-hidden />} onClick={() => ouvrir('rejeter')}>Rejeter</Bouton>}
        </div>
      )}
      {calculer && refusReprise && ligne.origine === 'trame' && <p className="tres-petit cch-texte-ambre" style={{ marginTop: 6 }}>Reprise impossible : {refusReprise}</p>}
      {reprise && (
        <div style={{ marginTop: 12 }}>
          <Alerte ton="succes" titre="Brouillons créés — non validés">
            {reprise.reperes || reprise.liaisons || reprise.fiches
              ? `${reprise.reperes} repère(s) de comptage (${reprise.types} type(s)), ${reprise.liaisons} liaison(s) frigorifique(s), ${reprise.lignes} ligne(s) (condensats, alimentations, bus) et ${reprise.fiches} fiche(s) système, marqués « issus de la proposition ».`
              : 'Reprise déjà faite : rien de nouveau n’a été créé (aucun doublon).'}
            {' '}La validation se fait comme d’habitude : comptage par le référent, métrés des cheminements, notice de charge. La proposition n’écrit jamais dans un devis.
            {reprise.avertissements.length > 0 && <ul className="tres-petit">{reprise.avertissements.map((a, i) => <li key={i}>{a}</li>)}</ul>}
            <span className="groupe-btn" style={{ marginTop: 8, display: 'flex' }}>
              <LienBouton compact to={`/etudes/${dossierId}/conception?vue=comptage`}>Ouvrir le comptage</LienBouton>
              <LienBouton compact to={`/etudes/${dossierId}/conception?vue=cheminements`}>Ouvrir les métrés et la charge</LienBouton>
            </span>
          </Alerte>
        </div>
      )}

      <Dialogue ouvert={!!action} fermer={() => setAction(null)}
        titre={action === 'retenir' ? `Retenir « ${ligne.libelle} »` : action === 'rejeter' ? `Rejeter « ${ligne.libelle} »` : action === 'validation' ? `Valider « ${ligne.libelle} »` : 'Reprendre comme base de travail'}
        pied={<><Bouton onClick={() => setAction(null)}>Annuler</Bouton>
          <Bouton variante={action === 'rejeter' ? 'danger' : 'principal'} chargement={envoi} disabled={desactive} onClick={envoyer}>
            {action === 'retenir' ? 'Retenir la variante' : action === 'rejeter' ? 'Rejeter la variante' : action === 'validation' ? 'Valider la variante' : 'Créer les brouillons'}
          </Bouton></>}>
        <div className="pile">
          {action === 'reprise' && (
            <>
              <p className="petit">TRAME va créer, à partir de cette variante, des <strong>brouillons non validés</strong> :</p>
              <ul className="petit">
                <li>repères de comptage par type, reliés au modèle et à l’ouvrage type ;</li>
                <li>liaisons frigorifiques tracées (diamètres, dérivations, montées) ;</li>
                <li>condensats, alimentations indicatives et bus ;</li>
                <li>fiches système pour la charge de fluide.</li>
              </ul>
              <p className="petit secondaire">Rien n’est validé ni envoyé au devis : la validation reste celle du comptage, des métrés et de la notice de charge. Relancer la reprise ne crée aucun doublon.{ligne.statut === 'calculee' ? ' La variante sera retenue.' : ''}</p>
            </>
          )}
          {action === 'retenir' && <p className="petit">La variante devient le choix du dossier (le précédent choix éventuel est retiré). Le motif est conservé dans l’historique.</p>}
          {action === 'rejeter' && <p className="petit">La variante reste visible, marquée « rejetée ». Le motif est conservé dans l’historique.</p>}
          {action === 'validation' && (
            <>
              <p className="petit">Relecture technique : chaque point « à vérifier » peut être levé avec un commentaire ; les autres restent ouverts et affichés comme tels.</p>
              <div className="pile prp-levees">
                {aVerifier.map((p) => (
                  <ChampTexte key={p.id} libelle={`${p.code} — ${p.message}`} rows={2} value={levees[p.id] ?? ''} placeholder="Commentaire de levée (laisser vide : reste ouvert)"
                    onChange={(e) => setLevees((x) => ({ ...x, [p.id]: e.target.value }))} />
                ))}
                {!aVerifier.length && <p className="petit">Aucun point « à vérifier ».</p>}
              </div>
            </>
          )}
          {(action === 'rejeter' || action === 'retenir' || action === 'reprise') && (
            <ChampTexte libelle={action === 'rejeter' ? 'Motif du rejet' : motifRequis ? 'Motif du choix (obligatoire : la variante n’est pas première)' : 'Motif (facultatif)'}
              rows={3} maxLength={1000} value={motif} onChange={(e) => setMotif(e.target.value)} />
          )}
          {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        </div>
      </Dialogue>
    </Panneau>
  );
}
